import { useState } from 'react';
import styled from 'styled-components';
import { collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import Modal from './Modal';
import Button from './Button';
import FormField from './shared/FormField';
import UsageLimitModal from './UsageLimitModal';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const Title = styled.h2`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  text-align: center;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const Label = styled.label`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`;

const Input = styled.input`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const PresetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const PresetButton = styled.button`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme, $isSelected }) => 
    $isSelected ? theme.colors.primary : theme.colors.border};
  border-radius: 4px;
  background: ${({ theme, $isSelected }) => 
    $isSelected ? `${theme.colors.primary}10` : theme.colors.white};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const BinCountInput = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
  
  input {
    width: 60px;
    text-align: center;
    padding: ${({ theme }) => theme.spacing.sm};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 4px;
    font-size: 1rem;
    margin: 0 ${({ theme }) => theme.spacing.sm};

    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.primary};
    }

    /* Hide spinner buttons */
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
`;

const BinCountButton = styled.button`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  transition: all 0.2s;
  font-size: 1.2rem;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => `${theme.colors.primary}10`};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: ${({ theme }) => theme.colors.background};
  }
`;

const PresetBinGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const PresetBinButton = styled.button`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme, $isSelected }) => 
    $isSelected ? theme.colors.primary : theme.colors.border};
  border-radius: 4px;
  background: ${({ theme, $isSelected }) => 
    $isSelected ? `${theme.colors.primary}10` : theme.colors.white};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const OptionalNote = styled.span`
  color: ${({ theme }) => theme.colors.text};
  opacity: 0.7;
  font-size: 0.9rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const RoomSetupModal = ({ isOpen, onClose, onRoomCreated }) => {
  const [roomName, setRoomName] = useState('');
  const [selectedBinCount, setSelectedBinCount] = useState(3);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, isPremium } = useAuth();

  const presetRooms = ['Garage', 'Kitchen', 'Bedroom', 'Office', 'Basement', 'Attic'];
  const presetBinCounts = [0, 1, 2, 3, 4, 5];

  const handleBinCountChange = (value) => {
    const newValue = Math.max(0, value);
    setSelectedBinCount(newValue);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!roomName.trim()) return;
    
    setIsSubmitting(true);

    try {
      // Check for duplicate room names
      const roomsQuery = query(
        collection(db, 'rooms'),
        where('userId', '==', user.uid),
        where('name', '==', roomName.trim())
      );
      const roomsSnapshot = await getDocs(roomsQuery);
      
      if (!roomsSnapshot.empty) {
        alert('A room with this name already exists');
        setIsSubmitting(false);
        return;
      }

      const roomData = {
        name: roomName.trim(),
        userId: user.uid,
        createdAt: new Date().toISOString(),
        binCount: selectedBinCount
      };

      const roomRef = await addDoc(collection(db, 'rooms'), roomData);

      // Create bins if binCount > 0
      if (selectedBinCount > 0) {
        const binsRef = collection(db, 'bins');
        const binPromises = Array.from({ length: selectedBinCount }, (_, i) => {
          return addDoc(binsRef, {
            roomId: roomRef.id,
            number: i + 1,
            name: null,
            createdAt: new Date().toISOString()
          });
        });

        await Promise.all(binPromises);
      }

      onRoomCreated(roomRef.id);
      onClose();
    } catch (error) {
      console.error('Error creating room:', error);
      alert('Failed to create room. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Form onSubmit={handleSubmit}>
        <Title>Add a Room</Title>
        
        <FormGroup>
          <Label>Select a preset or enter a custom room name:</Label>
          <PresetGrid>
            {presetRooms.map(preset => (
              <PresetButton
                key={preset}
                type="button"
                $isSelected={roomName === preset}
                onClick={() => setRoomName(preset)}
              >
                {preset}
              </PresetButton>
            ))}
          </PresetGrid>
          <FormField
            id="roomName"
            type="text"
            value={roomName}
            onChange={(e) => setRoomName(e.target.value)}
            label="Room Name"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Number of Bins</Label>
          <PresetBinGrid>
            {presetBinCounts.map(count => (
              <PresetBinButton
                key={count}
                type="button"
                $isSelected={selectedBinCount === count}
                onClick={() => setSelectedBinCount(count)}
              >
                {count === 0 ? 'No Bins' : `${count} Bin${count === 1 ? '' : 's'}`}
              </PresetBinButton>
            ))}
          </PresetBinGrid>
          {isPremium && (
            <BinCountInput>
              <BinCountButton
                type="button"
                onClick={() => handleBinCountChange(selectedBinCount - 1)}
                disabled={selectedBinCount <= 0}
              >
                -
              </BinCountButton>
              <input
                type="number"
                value={selectedBinCount}
                onChange={(e) => handleBinCountChange(parseInt(e.target.value) || 0)}
                min="0"
              />
              <BinCountButton
                type="button"
                onClick={() => handleBinCountChange(selectedBinCount + 1)}
              >
                +
              </BinCountButton>
            </BinCountInput>
          )}
          <OptionalNote>
            You can add or remove bins later
          </OptionalNote>
        </FormGroup>

        <FormGroup>
          <Label>Room Photo</Label>
          <OptionalNote>
            Optional - You can add a photo later
          </OptionalNote>
        </FormGroup>

        <ButtonContainer>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!roomName.trim() || isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create Room'}
          </Button>
        </ButtonContainer>
      </Form>
    </Modal>
  );
};

export default RoomSetupModal; 